import {useEffect} from 'react';

import {PAYMENT_METHODS} from '@optimaxdev/analytics/desktop/constants/paymentMethods';
import {prepareUpsellData} from '@optimaxdev/analytics/desktop/lib/ecommerce/helpers/index';
import {createEECPurchaseEvent} from '@optimaxdev/analytics/desktop/lib/ecommerce/typ/index';
import {pushDataLayer} from '@optimaxdev/analytics/desktop/lib/pushDataLayer/index';

import {useDispatch, useSelector} from 'react-redux';

import {TYP_UPSELL_TYPES} from 'constants/product';
import {openPopupPayment} from 'desktop/pages/success/store/reducers/orderLast/orderLast';
import {
    getActiveUpsell,
    getOrderLastData,
    getUpsellsState,
} from 'desktop/pages/success/store/selectors/selectors';
import {getAffiliateCode} from 'desktop/pages/success/store/selectors/typ/typ';
import {UpsellType} from 'desktop/pages/success/types/upsellsTypes';
import {useHostSelector} from 'hooks/hostHooks/hostHooks';
import {logWithSentry, logWithSentryEEC} from 'libs/sentry/sentry';
import {getRouter} from 'selectors/router';

const prepareEECEvent = ({
    affiliation,
    orderId,
    paymentMethod,
    sku,
    upsell,
}: {
    sku: string;
    orderId: string;
    paymentMethod: string;
    upsell: UpsellType;
    affiliation: string;
}) => {
    const upsellType = upsell.type === TYP_UPSELL_TYPES.ACCESSORY ? 'accessories' : 'upgrade';
    const actionField = {
        action: 'purchase',
        affiliation,
        coupon: '',
        id: orderId,
        revenue: (upsell.price || 0).toFixed(4),
        shipping: '0.0000',
        tax: '0.0000',
    };

    const products = [prepareUpsellData({...upsell, price: upsell.price.toFixed(4)}, sku)];

    const event = createEECPurchaseEvent({
        actionField,
        eventPostFix: upsellType,
        orderId,
        paymentMethod:
            paymentMethod === 'recurly_paypal' ? paymentMethod : PAYMENT_METHODS[paymentMethod],
        products,
    });

    return event;
};

/**
 * Hook for sending purchase analytics for upsell
 * @param {router: RouterType} context - context with router data
 */
export function useSendPurchaseAnalyticsForUpsell() {
    const {addedUpsell} = useSelector(getUpsellsState);
    const {paymentMethod: paymentMethodRaw} = useSelector(getOrderLastData);
    const router = useHostSelector(getRouter);
    const upsell = useSelector(getActiveUpsell);
    const dispatch = useDispatch();

    useEffect(() => {
        if (addedUpsell) {
            try {
                const {id, sku} = addedUpsell;
                const paymentMethod = paymentMethodRaw?.includes('ms_paymentsos')
                    ? 'ms_paymentsos'
                    : paymentMethodRaw;
                const {affiliate_code: affiliation = ''} = getAffiliateCode({router});

                const event = prepareEECEvent({
                    affiliation,
                    orderId: id,
                    paymentMethod: paymentMethod || '',
                    sku,
                    upsell,
                });
                pushDataLayer(event);
                logWithSentryEEC(`EEC_purchase_upgrade`, event);
                dispatch(openPopupPayment(false));
            } catch (error) {
                logWithSentry(new Error("Can't send purchase analytics"), error);
            }
        }
    }, [addedUpsell]);
}
